<template>
  <div class="permission-group">
    <Toggle
      :id="group.id"
      kind="primary"
      :name="group.id"
      size="large"
      :model-value="isToggledOn"
      @update:model-value="toggleGroup($event)"
    />

    <div class="permission-group__description">
      {{ group.name }}
    </div>

    <SelectField
      class="select"
      :options="abilityOptions"
      :disabled="!isToggledOn"
      placeholder=""
      size="md"
      :show-icon-prepend="false"
      placement="bottom-end"
      :match-trigger-width="false"
      multiple
    >
      <template #active-option>
        <div class="select__placeholder">
          {{ outOf }}
        </div>
      </template>

      <template #option="{ option }">
        <ListItemWithState
          class="select__item"
          :selected="option.selected"
          flair="soft"
          size="md"
          wrap
          full-width
        >
          <p
            class="select__item__title"
            @click="toggleAbility(option.id, !option.selected)"
          >
            <ToolTip
              :content="option.info"
              use-portal
            >
              {{ option.name }}
            </ToolTip>
          </p>
        </ListItemWithState>
      </template>
    </SelectField>
  </div>
</template>

<script lang="ts">
export default {
  name: 'PermissionGroupSelector',
}
</script>

<script setup lang="ts">
/**
 * Renders a permission group within the generalized key creation UI
 *
 * Has an expanded and collapsed state.
 *
 * The collapsed state allows selection of the entire group of permissions.
 * The expanded state allows selection of individual permissions.
 */
import { ref, computed } from 'vue'

import { useAuthStore } from '@/modules/Auth/useAuthStore'

import Toggle from '@/uiKit/Toggle/Toggle.vue'
import SelectField from '@/uiKit/SelectField/SelectField.vue'
import ListItemWithState from '@/uiKit/List/ListItemWithState.vue'

import { GROUPED_ABILITIES, ABILITY_INFO } from './data'
import type { AbilityID, AbilityGroup, AbilityOption, Group } from './types'
import ToolTip from '@/uiKit/ToolTip/ToolTip.vue'

const props = defineProps<{ group: Group }>()

const isToggledOn = ref(false)
const selectedAbilities = ref<AbilityID[]>([])

const emit = defineEmits<{
  (e: 'change', groupId: AbilityGroup, abilities: AbilityID[]): void
}>()

const authStore = useAuthStore()

const abilities = computed(() => {
  const actions = authStore.abilities.reduce(
    (acc, a) => acc.concat(a.actions as AbilityID[]),
    [] as AbilityID[],
  )

  return GROUPED_ABILITIES[props.group.id]
    .filter((action) => actions.includes(action))
    .map((action) => ({
      id: action,
      name: ABILITY_INFO[action].name,
      info: ABILITY_INFO[action].info,
    }))
})

const abilityOptions = computed((): AbilityOption[] =>
  abilities.value.map((ability) => ({
    ...ability,
    selected: selectedAbilities.value.includes(ability.id),
  })),
)

const outOf = computed((): string => {
  const selectedCount = abilityOptions.value.filter((o) => o.selected).length
  const totalCount = abilityOptions.value.length
  return `${selectedCount} / ${totalCount} authorisations`
})

const toggleGroup = (selected: boolean): void => {
  isToggledOn.value = selected

  if (selected) {
    selectedAbilities.value = abilities.value.map((a) => a.id)
  } else {
    selectedAbilities.value = []
  }

  emit('change', props.group.id, selectedAbilities.value)
}

const toggleAbility = (id: AbilityID, selected: boolean): void => {
  const index = selectedAbilities.value.indexOf(id)

  if (selected && index === -1) {
    selectedAbilities.value.push(id)
  }

  if (!selected && index >= 0) {
    selectedAbilities.value.splice(index, 1)
  }

  emit('change', props.group.id, selectedAbilities.value)
}
</script>

<style lang="scss" scoped>
.permission-group {
  @include row;
  align-items: center;
  justify-content: space-between;
  padding: 10px 0;

  &__description {
    @include typographyRegularBody200;
    color: var(--color-text-subtle);
    margin-left: 16px;
    flex: 1;
  }

  .select {
    width: 170px;
  }
  .select__placeholder {
    @include typographyRegularBody200;
  }

  .select {
    &__item {
      &__title {
        @include typographyRegularBody200;
      }

      &:last-child {
        margin-bottom: 2px;
      }
    }
  }
}
</style>
